import {IconProp} from '@fortawesome/fontawesome-svg-core'
import {faClock} from '@fortawesome/free-solid-svg-icons'
import {faPersonCircleXmark} from '@fortawesome/pro-light-svg-icons'
import moment from 'moment'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import styled from 'styled-components'

import {useAppDispatch, useAppSelector} from '../../../App/Hooks/reduxHooks'
import SecondaryMenuUpdater from '../../../App/SecondaryMenuUpdater'
import {Box} from '../../../Common/Box'
import {Icon} from '../../../Common/Icon'
import {IUser} from '../../../User/DataStore/IUser'
import {IUserHr} from '../../../User/UsersHr/interfaces/IUserHr'
import {IPunchclockTime} from '../Client/IPunchclockTime'
import {PersonCrud} from '../Overview/Overview'
import {endPunchclockTime, startPunchclockTime} from '../Store/Actions'

import {
    ActionIcon,
    BoxContainer,
    BoxLabel,
    ContainerBox,
    IconWrapper,
    NameBox,
    OutOfServiceIcon,
    OutOfServiceToolTip,
    StyledCard,
    StyledFlex,
    StyledImage,
    StyledLink,
    StyledText,
    ToolTipText,
} from './style'
import style from './style.scss'

type Props = {
  currentLoggedUser: IUser
  user: IUserHr
  setPersonCrud: (crudObject: PersonCrud) => void
}

const StyledIcon = styled(Icon)`
  position: absolute;
  margin-left: 8px;
`

export const UserCard: React.FC<Props> = ({
  currentLoggedUser,
  user,
  setPersonCrud,
}) => {
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [isOutOfServiceHovered, setIsOutOfServiceHovered] =
    useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const canManageWorkingHours = currentLoggedUser.permissions.hrTimeManage
  const canManageAbsences = currentLoggedUser.permissions.hrAbsencesManage
  const canManageUsers = currentLoggedUser.permissions.hrUsersManage

  const iconRef = useRef<HTMLDivElement>(null)
  const punchclockTimes = useAppSelector(state => {
    if (!state.punchclock.times) {
      return []
    }

    return state.punchclock.times
      .filter(time => {
        return (
          time.userId === user.id &&
          moment(time.punchStartedAt).isSame(moment(), 'day')
        )
      })
      .sort((a, b) =>
        moment(a.punchStartedAt).isBefore(b.punchStartedAt) ? -1 : 1,
      )
  })

  const establishment = useAppSelector(state => state.establishment.active)

  const start = () => {
    setIsUpdating(true)
    dispatch(startPunchclockTime(user)).then(() => setIsUpdating(false))
  }

  const end = () => {
    setIsUpdating(true)
    dispatch(endPunchclockTime(user)).then(() => setIsUpdating(false))
  }

  let hasOpenPunchclockTime =
    punchclockTimes.filter(time => null === time.punchEndedAt).length > 0

  useEffect(() => {
    SecondaryMenuUpdater.setActive('persons')
  }, [])

  return (
    <StyledFlex flexDirection="row" mb={16}>
      <StyledCard p={3} bg="lights.3" shadow="darks.3">
        <div className={style.avatar}>
          <StyledImage src={`${user.avatar}&w=144&h=144&fit=crop`} />
          {!isUpdating &&
            establishment &&
            establishment.permissions.includes('punchclock') &&
            user.hasToPunch && (
              <div
                className={`${style.overlay} ${
                  hasOpenPunchclockTime ? style.destructive : style.success
                }`}
                onClick={() => (hasOpenPunchclockTime ? end() : start())}>
                {hasOpenPunchclockTime ? t('End') : t('Start')}
              </div>
            )}
        </div>
        <ContainerBox color={'darks.3'} width={'100%'}>
          <BoxContainer display={'flex'} width={'100%'}>
            <NameBox mb={1} width={'100%'}>
              {user.firstName} {user.lastName && user.lastName}
              {user.hasToPunch && <StyledIcon icon={faClock as IconProp} />}
            </NameBox>
            <Box display={'flex'}>
              {user.outOfServiceAt != null && (
                <IconWrapper
                  ref={iconRef}
                  onMouseEnter={() => setIsOutOfServiceHovered(true)}
                  onMouseLeave={() => setIsOutOfServiceHovered(false)}>
                  <OutOfServiceIcon icon={faPersonCircleXmark} />
                  {isOutOfServiceHovered && (
                    <OutOfServiceToolTip>
                      <ToolTipText>
                        {t('This person would bet out of service at')}{' '}
                        <b>{user.outOfServiceAt}</b>
                      </ToolTipText>
                    </OutOfServiceToolTip>
                  )}
                </IconWrapper>
              )}
              {canManageUsers && (
                <>
                  <ActionIcon
                    icon={'pen'}
                    onClick={() =>
                      setPersonCrud({ person: user, state: 'edit' })
                    }
                  />
                  <StyledLink to={'/hr/knowledge/' + user.uuid}>
                    <ActionIcon icon={'gear'} />
                  </StyledLink>

                  <ActionIcon
                    icon={'xmark'}
                    onClick={() =>
                      setPersonCrud({ person: user, state: 'delete' })
                    }
                  />
                </>
              )}
            </Box>
          </BoxContainer>

          {user.startsAt &&
            moment(user.startsAt).isSameOrAfter(moment(), 'day') && (
              <Box mb={0}>
                <StyledText>
                  {t('Date in service')} :{' '}
                  {moment(user.startsAt).format('DD/MM/YYYY')}
                </StyledText>
              </Box>
            )}

          <Box>
            {canManageWorkingHours ? (
              <Box mb={1}>
                <BoxLabel>
                  <a href={'/hr/working-hours/person/' + user.uuid}>
                    {t('Working hours')}
                  </a>
                </BoxLabel>
              </Box>
            ) : null}
          </Box>

          {/*<Box>*/}
          {/*  {canManageWorkingHours ? (*/}
          {/*    <Box mb={1}>*/}
          {/*      <BoxLabel>*/}
          {/*        <a href={'/hr/persons/contracts/' + user.uuid}>*/}
          {/*          {t('Contracts')}*/}
          {/*        </a>*/}
          {/*      </BoxLabel>*/}
          {/*    </Box>*/}
          {/*  ) : null}*/}
          {/*</Box>*/}

          <Box>
            {canManageAbsences ? (
              <Box mb={1}>
                <BoxLabel>
                  <Link to={'/hr/absences/' + user.uuid}>
                    {t('Report absence')}
                  </Link>
                </BoxLabel>
              </Box>
            ) : null}
          </Box>

          {establishment && establishment.permissions.includes('punchclock') ? (
            <Box>
              <div className={style.punchclock}>
                <div className={style.label}>{t('Punchclock')}</div>
                {punchclockTimes.map((time: IPunchclockTime) => {
                  hasOpenPunchclockTime =
                    hasOpenPunchclockTime || null === time.punchEndedAt

                  return (
                    <>
                      {moment(time.punchStartedAt).format('HH:mm')}
                      &nbsp;-&nbsp;
                      {time.punchEndedAt ? (
                        moment(time.punchEndedAt).format('HH:mm')
                      ) : isUpdating ? null : (
                        <a onClick={end}>{t('End')}</a>
                      )}
                      <br />
                    </>
                  )
                })}
                {!hasOpenPunchclockTime && !isUpdating ? (
                  <a onClick={start}>{t('Start')}</a>
                ) : null}
              </div>
            </Box>
          ) : null}
        </ContainerBox>
      </StyledCard>
    </StyledFlex>
  )
}
